import React from "react";
import { View, ScrollView, TouchableOpacity, Platform } from "react-native";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import { AppText } from "@/components/ui/AppText";
import { Badge } from "@/components/ui/Badge";
import { Icon } from "@/components/ui/Icon";
import { PageFooter } from "@/components/ui/PageFooter";
import { useI18n } from "@/context/I18nContext";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { vendorApi, type VendorApplicationData, type ApplicationStageId } from "@/lib/api/vendor";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

// ─── Query key ────────────────────────────────────────────────────────────────

export const VENDOR_APPLICATION_QUERY_KEY = ["vendor", "application"] as const;

// ─── Stage config ─────────────────────────────────────────────────────────────

interface StageConfig {
  labelKey: TranslationKeys;
  icon: React.ComponentProps<typeof MaterialCommunityIcons>["name"];
  iconColor: string;
}

const STAGE_CONFIG: Record<ApplicationStageId, StageConfig> = {
  legal_representative: {
    labelKey: "vendor.application.stages.legalRepresentative",
    icon: "account-circle",
    iconColor: "text-blue-50",
  },
  business_information: {
    labelKey: "vendor.application.stages.businessInformation",
    icon: "office-building",
    iconColor: "text-blue-50",
  },
  product_compliance: {
    labelKey: "vendor.application.stages.productCompliance",
    icon: "clipboard-check",
    iconColor: "text-blue-50",
  },
  financial_settlement: {
    labelKey: "vendor.application.stages.financialSettlement",
    icon: "bank",
    iconColor: "text-blue-50",
  },
  branding_localization: {
    labelKey: "vendor.application.stages.brandingLocalization",
    icon: "palette",
    iconColor: "text-blue-50",
  },
} as const;

const STAGE_ORDER: ApplicationStageId[] = [
  "legal_representative",
  "business_information",
  "product_compliance",
  "financial_settlement",
  "branding_localization",
];

// ─── Date row sub-component ───────────────────────────────────────────────────

function DateValue({
  info,
}: {
  info: { relative: string; absolute: string; time: string } | null;
}) {
  if (info == null) {
    return (
      <AppText weight="medium" className="text-base text-dark-text-10">
        {"–\u2013"}
      </AppText>
    );
  }
  return (
    <View className="items-end gap-1">
      <AppText weight="medium" className="text-base text-dark-text-50">
        {info.relative}
      </AppText>
      <View className="flex-row items-center gap-1.5">
        <AppText weight="medium" className="text-base text-dark-text-50">
          {info.absolute}
        </AppText>
        <View className="size-1.5 rounded-full bg-dark-text-10" />
        <AppText weight="medium" className="text-base text-dark-text-50">
          {info.time}
        </AppText>
      </View>
    </View>
  );
}

// ─── Main screen ─────────────────────────────────────────────────────────────

export default function VendorApplicationScreen() {
  const { t, isRTL } = useI18n();
  const { top } = useSafeAreaInsets();
  const isWide = useIsWideScreen();
  const router = useRouter();
  const { user } = useAuth();
  const { locale } = useI18n();
  const applicationId = user?.applicationId ?? null;

  const { data } = useQuery<VendorApplicationData>({
    queryKey: [...VENDOR_APPLICATION_QUERY_KEY, applicationId, locale],
    queryFn: () => vendorApi.getApplicationData(applicationId!, locale),
    enabled: !!applicationId,
  });

  const companyName = data?.companyName ?? "";
  const primaryIndustry = data?.primaryIndustry ?? "";
  const lastActivity = data?.lastActivity ?? null;
  const dateSubmitted = data?.dateSubmitted ?? null;
  const dateApproved = data?.dateApproved ?? null;
  const stages = data?.stages ?? [];

  return (
    <ScrollView
      className={`flex-1 ${isWide ? "bg-stroke-20" : "bg-white"}`}
      contentContainerStyle={{ flexGrow: 1 }}
      testID="vendor-application"
    >
      {/* ── Desktop: white page heading ─────────────────────────────── */}
      {isWide && Platform.OS === "web" && (
        <View className="bg-white px-20 pt-6 pb-6 border-b border-stroke-50 items-start">
          <AppText
            weight="bold"
            className="text-[29px] leading-[32px] tracking-[-0.29px] text-dark-text-50"
          >
            {t("vendor.dashboard.nav.application")}
          </AppText>
          <AppText weight="medium" className="text-base text-supporting-text-40 mt-1">
            {t("vendor.application.subtitle")}
          </AppText>
        </View>
      )}

      {/* ── Mobile: top title bar ───────────────────────────────────── */}
      {(!isWide || Platform.OS !== "web") && (
        <View className="bg-white px-page-x pb-6 items-start" style={{ paddingTop: top + 16 }}>
          <AppText
            weight="semibold"
            className="text-[29px] leading-[32px] tracking-[-0.29px] text-dark-text-50"
          >
            {t("vendor.dashboard.nav.application")}
          </AppText>
        </View>
      )}

      {/* ── Content ─────────────────────────────────────────────────── */}
      <View className={`gap-6 ${isWide ? "px-20 py-6" : "pb-4"}`}>
        {/* ── Company Info section ─────────────────────────────────── */}
        <View
          testID="company-info-section"
          className={`bg-white overflow-hidden ${
            isWide ? "rounded-lg border border-stroke-50" : "border-t border-stroke-50"
          }`}
        >
          {/* Section header: company name */}
          <View
            className={cn(
              "items-start w-full",
              isWide ? "bg-stroke-30 px-10 py-4" : "bg-stroke-30 px-page-x py-3"
            )}
          >
            <AppText
              weight="bold"
              className="text-sm tracking-[0.14px] uppercase text-dark-text-50"
            >
              {companyName.toUpperCase()}
            </AppText>
          </View>
          <View className="border-t border-stroke-50" />

          {/* Info rows */}
          <View className={isWide ? "px-4" : "px-2"}>
            {/* Primary Industry */}
            <View
              className={`flex-row items-center justify-between gap-3 ${isWide ? "px-6 py-5" : "px-4 py-5"}`}
              testID="info-row-industry"
            >
              <AppText weight="medium" className="text-base text-supporting-text-40">
                {t("vendor.application.companyInfo.primaryIndustry")}
              </AppText>
              <AppText weight="medium" className="text-base text-dark-text-50">
                {primaryIndustry}
              </AppText>
            </View>
            <View className="border-b border-stroke-50" />

            {/* Last Activity */}
            <View
              className={`flex-row items-start justify-between gap-3 ${isWide ? "px-6 py-5" : "px-4 py-5"}`}
              testID="info-row-last-activity"
            >
              <AppText weight="medium" className="text-base text-supporting-text-40">
                {t("vendor.application.companyInfo.lastActivity")}
              </AppText>
              <DateValue info={lastActivity} />
            </View>
            <View className="border-b border-stroke-50" />

            {/* Date Submitted */}
            <View
              className={`flex-row items-start justify-between gap-3 ${isWide ? "px-6 py-5" : "px-4 py-5"}`}
              testID="info-row-date-submitted"
            >
              <AppText weight="medium" className="text-base text-supporting-text-40">
                {t("vendor.application.companyInfo.dateSubmitted")}
              </AppText>
              <DateValue info={dateSubmitted} />
            </View>
            <View className="border-b border-stroke-50" />

            {/* Date Approved */}
            <View
              className={`flex-row items-start justify-between gap-3 ${isWide ? "px-6 py-5" : "px-4 py-5"}`}
              testID="info-row-date-approved"
            >
              <AppText weight="medium" className="text-base text-supporting-text-40">
                {t("vendor.application.companyInfo.dateApproved")}
              </AppText>
              <DateValue info={dateApproved} />
            </View>
          </View>
        </View>

        {/* ── Application Stages section ───────────────────────────── */}
        <View
          testID="application-stages-section"
          className={`bg-white overflow-hidden ${
            isWide ? "rounded-lg border border-stroke-50" : "border-t border-stroke-50"
          }`}
        >
          {/* Section header */}
          <View
            className={cn(
              "items-start w-full",
              isWide ? "bg-stroke-30 px-10 py-4" : "bg-stroke-30 px-page-x py-3"
            )}
          >
            <AppText
              weight="bold"
              className="text-sm tracking-[0.14px] uppercase text-dark-text-50"
            >
              {t("vendor.application.stages.title")}
            </AppText>
          </View>
          <View className="border-t border-stroke-50" />

          {/* Stage rows */}
          <View>
            {STAGE_ORDER.map((stageId, i) => {
              const config = STAGE_CONFIG[stageId];
              const stageData = stages.find((s) => s.id === stageId);
              const status = stageData?.status ?? "not_started";

              const badgeVariant =
                status === "completed"
                  ? "filled-success"
                  : status === "in_progress" || status === "update_required"
                    ? "filled-warning"
                    : "default";

              const statusLabelKey: TranslationKeys =
                status === "completed"
                  ? "vendor.application.stages.completed"
                  : status === "in_progress"
                    ? "vendor.application.stages.inProgress"
                    : status === "update_required"
                      ? "vendor.application.stages.updateRequired"
                      : "vendor.application.stages.notStarted";

              return (
                <React.Fragment key={stageId}>
                  <TouchableOpacity
                    testID={`stage-row-${stageId}`}
                    className={`flex-row items-start gap-4 ${isWide ? "px-6 py-5" : "px-4 py-5"}`}
                    accessibilityRole="button"
                    onPress={() => {
                      router.push({
                        pathname: "/(vendor)/application/[stage]",
                        params: { stage: stageId },
                      });
                    }}
                  >
                    {/* Icon */}
                    <Icon
                      as={MaterialCommunityIcons}
                      name={config.icon}
                      size={32}
                      className={config.iconColor}
                    />

                    {/* Label + badge */}
                    <View className="flex-1 items-start gap-1.5">
                      <AppText weight="semibold" className="text-base text-dark-text-50">
                        {t(config.labelKey)}
                      </AppText>
                      <Badge
                        variant={badgeVariant}
                        shape="rounded"
                        label={statusLabelKey}
                        testID={`stage-badge-${stageId}`}
                      />
                    </View>

                    {/* Chevron */}
                    <Icon
                      as={MaterialCommunityIcons}
                      name={isRTL ? "chevron-left" : "chevron-right"}
                      size={22}
                      className="text-supporting-text-40 shrink-0"
                    />
                  </TouchableOpacity>
                  {i < STAGE_ORDER.length - 1 && <View className="border-b border-stroke-50" />}
                </React.Fragment>
              );
            })}
          </View>
        </View>
      </View>

      <PageFooter />
    </ScrollView>
  );
}
